// MENU-MEASURE-1 — EVERY MENU ITEM HAS A MEASURE OR A WRITTEN REASON.
//
// ─── WHAT WAS MEASURED, AND HOW IT DIFFERS FROM THE BRIEF ──────────────────
//
// The brief says 21 of 68 items have a measured usage key. Counted from
// `navConfig` itself: there are **86 nodes** - 13 top level sections and 73
// children - and **24** carry a measure, all of them the settings children
// SETTINGS-SPLIT-1 bucketed. So 62 have none, and every one is a section or a
// non-settings child.
//
// The shape of the finding is the brief's; the numbers are bigger.
//
// ─── THE THREE ANSWERS, AND WHY THERE ARE THREE ────────────────────────────
//
// An item that cannot be measured is a DECISION, not an accident, so there is
// nowhere here to put "we did not get to it":
//
//   measure      - a table that proves the account uses it. Counted, below.
//   dayOne       - kept even at zero, with the reason. This is SETTINGS-SPLIT-1's
//                  `platform` bucket applied to the whole menu: a brand new
//                  account has used nothing, and hiding the thing it is supposed
//                  to start with is how you make an empty product emptier.
//   unmeasurable - there is no honest signal, and that is stated rather than
//                  defaulted on quietly.
//
// A section (top level) declares none of the three: it shows when any of its
// children shows, which is derived, not decided. Making a section declare its
// own measure would be a second opinion about the same question.

/** A table whose rows prove this account uses the thing. */
export interface MenuMeasure {
  /** The table, as PostgREST names it. */
  table: string
  /** What a row in it means, in one clause. */
  proves: string
}

export interface MenuItem {
  id: string
  /** The section it lives under; null for a section itself. */
  parent: string | null
  measure?: MenuMeasure
  /** Kept even at zero. The reason is the whole point of the field. */
  dayOne?: string
  /** No honest signal exists. Stated, never defaulted on quietly. */
  unmeasurable?: string
}

/**
 * THE THIRTEEN SECTIONS. Derived, never declared: a section is on screen when
 * one of its children is.
 */
export const MENU_SECTIONS: readonly string[] = [
  'home',
  'messages',
  'schedule',
  'sales',
  'customers',
  'money',
  'website',
  'marketing',
  'automations',
  'team',
  'settings',
  'admin',
  'connectors',
]

export const MENU_CATALOGUE: readonly MenuItem[] = [
  // ─── MESSAGES ──────────────────────────────────────────────────────────
  {
    id: 'messages_inbox',
    parent: 'messages',
    dayOne:
      'The inbox is where a lead lands. An account with none still needs somewhere for the first one to arrive.',
  },
  {
    id: 'messages_calls',
    parent: 'messages',
    measure: { table: 'call_logs', proves: 'a call was placed or received' },
  },
  {
    id: 'messages_phone_setup',
    parent: 'messages',
    dayOne:
      'TEXTING-TRUTH-1 measured zero of fifty one accounts able to text. Hiding the one screen that fixes that from everybody who has not fixed it is a trap.',
  },
  {
    id: 'messages_chat_widget',
    parent: 'messages',
    measure: { table: 'site_chat_messages', proves: 'somebody used the website chat' },
  },
  {
    id: 'messages_email_domain',
    parent: 'messages',
    dayOne:
      'MENU-WIRE-1 - a setup screen, like phone setup beside it. It is measured on the very rows it exists to create, and reads zero across all fifty accounts, so it was hidden from every account that had not already done it.',
  },

  // ─── SCHEDULE ──────────────────────────────────────────────────────────
  {
    id: 'schedule_jobs',
    parent: 'schedule',
    dayOne:
      'Jobs are the thing the business does. A service business with none has not started yet, not decided against it.',
  },
  {
    id: 'schedule_dispatch',
    parent: 'schedule',
    measure: { table: 'daily_dispatch_plans', proves: 'a day was dispatched' },
  },
  {
    id: 'schedule_tasks',
    parent: 'schedule',
    measure: { table: 'tasks', proves: 'a task exists' },
  },
  {
    id: 'schedule_time_clock',
    parent: 'schedule',
    measure: { table: 'time_entries', proves: 'somebody clocked in' },
  },
  {
    id: 'schedule_my_schedule',
    parent: 'schedule',
    measure: { table: 'jobs', proves: 'there is work to be scheduled at all' },
  },

  // ─── SALES ─────────────────────────────────────────────────────────────
  {
    id: 'sales_leads',
    parent: 'sales',
    dayOne:
      'A lead is the first thing that happens to a new account. Hiding it until one arrives means it is missing on the day it matters most.',
  },
  {
    id: 'sales_quotes',
    parent: 'sales',
    measure: { table: 'quotes', proves: 'a quote was written' },
  },
  {
    id: 'sales_pipeline',
    parent: 'sales',
    measure: { table: 'opportunities', proves: 'an opportunity is being tracked' },
  },
  {
    id: 'sales_map',
    parent: 'sales',
    measure: { table: 'canvass_pins', proves: 'somebody knocked on a door' },
  },
  {
    id: 'sales_analytics',
    parent: 'sales',
    measure: { table: 'opportunities', proves: 'there is sales activity to analyse' },
  },

  // ─── CUSTOMERS ─────────────────────────────────────────────────────────
  {
    id: 'customers_customers',
    parent: 'customers',
    dayOne:
      'The customer list is the spine of a CRM. An empty one is a state, not a reason to remove it.',
  },
  {
    id: 'customers_services',
    parent: 'customers',
    measure: { table: 'services', proves: 'a service was defined' },
  },
  {
    id: 'customers_service_plans',
    parent: 'customers',
    measure: { table: 'service_plans', proves: 'a recurring plan was created' },
  },
  {
    id: 'customers_agreements',
    parent: 'customers',
    measure: { table: 'agreement_templates', proves: 'an agreement template exists' },
  },
  {
    id: 'customers_import',
    parent: 'customers',
    dayOne:
      'Import is a day-one screen by definition: it exists for the account that has nothing in it yet, so measuring use to decide whether to show it is backwards.',
  },

  // ─── MONEY ─────────────────────────────────────────────────────────────
  {
    id: 'money_invoices',
    parent: 'money',
    measure: { table: 'invoices', proves: 'an invoice was raised' },
  },
  {
    id: 'money_payouts',
    parent: 'money',
    measure: { table: 'merchant_withdrawals', proves: 'money was taken out' },
  },
  { id: 'money_revenue', parent: 'money', measure: { table: 'payments', proves: 'money came in' } },
  {
    id: 'money_payment_account',
    parent: 'money',
    dayOne:
      'WHOP-TRUTH-1 measured eleven connected accounts and five verified. The screen that gets an account paid cannot be gated on it already being paid.',
  },
  {
    id: 'money_plan_and_usage',
    // THE-DIVIDER-HOLDS-ONLY-WHAT-IS-RULED-1 — moved into Settings with the page.
    parent: 'settings',
    unmeasurable:
      'Every account has a plan and a credit balance from the moment it exists, so a row proves nothing about whether the merchant looks at this. There is no page-view telemetry to substitute, and inventing one would be a guess. Stated rather than defaulted on quietly.',
  },
  {
    id: 'money_fees',
    // THE-DIVIDER-HOLDS-ONLY-WHAT-IS-RULED-1 — moved into Settings with the page.
    parent: 'settings',
    unmeasurable:
      'Fees are read, never written, so the account generates no row by using this. The only honest signal would be a page view and there is none.',
  },

  // ─── WEBSITE ───────────────────────────────────────────────────────────
  {
    id: 'website_builder',
    parent: 'website',
    dayOne:
      'MENU-WIRE-1 - the way IN. Every website child was measured on a table only the website section can create rows in, so at zero the section vanished and there was no route back to build a first site. Measured: 3 of 50 accounts have one.',
  },
  {
    id: 'website_analytics',
    parent: 'website',
    measure: { table: 'site_views', proves: 'the site was visited' },
  },

  // ─── MARKETING ─────────────────────────────────────────────────────────
  {
    id: 'marketing_return',
    parent: 'marketing',
    measure: { table: 'marketing_costs', proves: 'spend was recorded to measure return against' },
  },
  {
    id: 'marketing_campaigns',
    parent: 'marketing',
    dayOne:
      'MENU-WIRE-1 - the way IN to marketing. All eight marketing children were measured, so an account that had never marketed lost the section permanently and could not start. Measured: 0 of 50 accounts have a campaign.',
  },
  {
    id: 'marketing_reactivation',
    parent: 'marketing',
    measure: { table: 'reengagement_messages', proves: 'a reactivation message went out' },
  },
  {
    id: 'marketing_reviews',
    parent: 'marketing',
    measure: { table: 'review_requests', proves: 'a review was asked for' },
  },
  {
    id: 'marketing_review_settings',
    parent: 'marketing',
    measure: { table: 'reputation_settings', proves: 'review handling was configured' },
  },
  {
    id: 'marketing_audiences',
    parent: 'marketing',
    measure: { table: 'workflow_audiences', proves: 'an audience was built' },
  },
  {
    id: 'marketing_ai_studio',
    parent: 'marketing',
    measure: { table: 'merchant_artifacts', proves: 'something was generated' },
  },
  {
    id: 'marketing_ai_history',
    parent: 'marketing',
    measure: {
      table: 'merchant_artifacts',
      proves: 'there is generated work to have a history of',
    },
  },

  // ─── AUTOMATIONS ───────────────────────────────────────────────────────
  {
    id: 'automations_automations',
    parent: 'automations',
    dayOne:
      'MENU-WIRE-1 - the way IN to automations. Both children were measured, so the section disappeared at zero with no way to build a first workflow. Measured: 5 of 50 accounts have one.',
  },
  {
    // MARKETPLACE-BROWSE-1 — kept at zero, and zero is the point.
    //
    // MARKETPLACE-MENU-1 — now a top-level section with NO CHILDREN, so it is
    // declared here by its dayOne reason rather than in MENU_SECTIONS as "a
    // section, derived from its children". Both answers may not apply to one
    // id: `duplicateDeclarations` fails a menu item that has two.
    id: 'marketplace',
    parent: null,
    dayOne:
      'A brand new account has used nothing, and this is the screen that fixes that. ' +
      'Measuring it by usage would hide the marketplace from exactly the account ' +
      'that needs it and show it only to the one that already found everything.',
  },
  {
    id: 'automations_connections',
    parent: 'automations',
    measure: { table: 'business_connections', proves: 'an outside service was connected' },
  },

  // ─── TEAM ──────────────────────────────────────────────────────────────
  {
    id: 'team_members',
    parent: 'team',
    dayOne:
      'Every account has exactly one team member on the day it is created, so a count proves nothing, and this is the screen that adds the second.',
  },
  {
    id: 'team_leaderboard',
    parent: 'team',
    measure: { table: 'commissions', proves: 'there is performance to rank' },
  },
  {
    id: 'team_commissions',
    parent: 'team',
    measure: { table: 'commission_rules', proves: 'commission was set up' },
  },
  {
    id: 'team_payroll',
    parent: 'team',
    measure: { table: 'paychecks', proves: 'somebody was paid' },
  },
  {
    id: 'team_my_commissions',
    parent: 'team',
    measure: { table: 'commissions', proves: 'this account runs commission at all' },
  },
  {
    id: 'team_my_paychecks',
    parent: 'team',
    measure: { table: 'paychecks', proves: 'this account runs payroll at all' },
  },

  // ─── ADMIN ─────────────────────────────────────────────────────────────
  // Platform staff only. Role decides these, not activity, and a merchant never
  // sees one whatever their account has done.
  {
    id: 'admin_accounts',
    parent: 'admin',
    unmeasurable:
      'Platform staff only. Visibility is decided by role, and no merchant activity can turn it on or off.',
  },
  {
    id: 'admin_affiliates',
    parent: 'admin',
    unmeasurable: 'Platform staff only. Role decides it, not activity.',
  },
  {
    id: 'admin_onboarding',
    parent: 'admin',
    unmeasurable: 'Platform staff only. Role decides it, not activity.',
  },
  {
    id: 'admin_health',
    parent: 'admin',
    unmeasurable: 'Platform staff only. Role decides it, not activity.',
  },
  {
    id: 'admin_pricing',
    parent: 'admin',
    unmeasurable: 'Platform staff only. Role decides it, not activity.',
  },

  // ─── CONNECTORS ────────────────────────────────────────────────────────
  {
    id: 'connectors',
    parent: null,
    dayOne:
      'MENU-WIRE-1 - the way IN. Connectors is a single-child section measured on business_connections, which reads ZERO across all fifty accounts, so it was hidden from everybody and nobody could ever make a first connection.',
  },
]

/** Every id the catalogue covers, for the guard. */
export const CATALOGUED_IDS: ReadonlySet<string> = new Set(MENU_CATALOGUE.map((m) => m.id))
